/**
 * Resuelve el valor de una celda cuando tableConfig usa claves tipo "tabla.campo"
 * pero la API devuelve alias SQL distintos (p. ej. volumen_corteza_especies).
 * También normaliza comillas tipográficas en nombres de columnas.
 */

const SMART_QUOTES = /[\u201c\u201d\u2018\u2019\u00ab\u00bb]/g

export function normalizeColumnKey(k: string): string {
  return k.replace(SMART_QUOTES, '"').replace(/"/g, '').trim()
}

/** Pares tabla.campo dentro de expresiones SQL (p. ej. SUM(... existencias_forestales.num_pies ...)). */
const DOTTED_SQL_FIELD = /\b([a-zA-Z_][a-zA-Z0-9_]*)\.([a-zA-Z_][a-zA-Z0-9_]*)\b/g

/**
 * Sufijos de campo (parte tras el punto) ordenados: primero los que aparecen más a la derecha en el texto.
 */
export function dottedSqlFieldSuffixes(columnKey: string): string[] {
  const found: { idx: number; field: string }[] = []
  let m: RegExpExecArray | null
  const re = new RegExp(DOTTED_SQL_FIELD.source, 'g')
  while ((m = re.exec(columnKey)) !== null) {
    found.push({ idx: m.index, field: normalizeColumnKey(m[2]) })
  }
  found.sort((a, b) => b.idx - a.idx)
  const out: string[] = []
  const seen = new Set<string>()
  for (const { field } of found) {
    if (!seen.has(field)) {
      seen.add(field)
      out.push(field)
    }
  }
  return out
}

/**
 * Identificadores en expresiones tipo SUM(COALESCE(num_pies, 0)) sin tabla.campo;
 * prioriza el que aparece más a la derecha y existe en las claves del row.
 */
const SQL_WORD = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g

const SQL_EXPR_NOISE_IDS = new Set(
  [
    'sum',
    'count',
    'avg',
    'min',
    'max',
    'coalesce',
    'nullif',
    'null',
    'distinct',
    'cast',
    'filter',
    'over',
    'within',
    'extract',
    'trim',
    'upper',
    'lower',
    'substring',
    'concat',
    'replace',
    'round',
    'floor',
    'ceil',
    'interval',
    'true',
    'false',
    'and',
    'or',
    'not',
    'case',
    'when',
    'then',
    'else',
    'end',
    'array',
    'json',
    'row',
  ]
)

function sqlIdentifiersMatchingRowKeys(
  columnKey: string,
  keys: string[]
): string[] {
  const rowNorms = new Set(keys.map((k) => normalizeColumnKey(k)))

  const hits: { idx: number; norm: string }[] = []
  let m: RegExpExecArray | null
  const re = new RegExp(SQL_WORD.source, 'g')
  while ((m = re.exec(columnKey)) !== null) {
    const norm = normalizeColumnKey(m[1])
    if (!norm || SQL_EXPR_NOISE_IDS.has(norm.toLowerCase())) continue
    if (!rowNorms.has(norm)) continue
    hits.push({ idx: m.index, norm })
  }
  hits.sort((a, b) => b.idx - a.idx)
  const out: string[] = []
  const seen = new Set<string>()
  for (const { norm } of hits) {
    if (!seen.has(norm)) {
      seen.add(norm)
      out.push(norm)
    }
  }
  return out
}

function findRowKeyByLastSegmentNorm(
  keys: string[],
  lastNorm: string
): string | undefined {
  if (!lastNorm || /[(),\s]/.test(lastNorm)) {
    // lastNorm inválido p. ej. "num_pies, 0))" tras split('.') en expresiones SQL
    return undefined
  }

  for (const k of keys) {
    const rk = normalizeColumnKey(k)
    if (rk === lastNorm) return k
  }

  for (const k of keys) {
    const rk = normalizeColumnKey(k)
    if (
      lastNorm.length >= 2 &&
      (rk.startsWith(lastNorm + '_') || rk === lastNorm)
    ) {
      return k
    }
  }

  for (const k of keys) {
    const rk = normalizeColumnKey(k)
    if (
      lastNorm.length >= 3 &&
      (rk.endsWith('_' + lastNorm) || rk.endsWith(lastNorm))
    ) {
      return k
    }
  }

  if (lastNorm.length >= 6) {
    for (const k of keys) {
      const rk = normalizeColumnKey(k)
      if (rk.includes(lastNorm)) return k
    }
  }

  return undefined
}

/**
 * Obtiene el valor en `row` que corresponde a `columnKey` del tableConfig.
 */
export function resolveTableCellValue(
  row: Record<string, unknown>,
  columnKey: string
): unknown {
  if (row == null || typeof row !== 'object') return undefined

  // 1) Clave exacta
  if (columnKey in row && row[columnKey] !== undefined) {
    return row[columnKey]
  }

  const keys = Object.keys(row)
  const colNorm = normalizeColumnKey(columnKey)

  // 2) Alguna clave del row coincide normalizada con columnKey completo
  for (const k of keys) {
    if (normalizeColumnKey(k) === colNorm) return row[k]
  }

  const last =
    columnKey.includes('.') ? columnKey.split('.').pop()!.trim() : columnKey
  const lastNorm = normalizeColumnKey(last)

  let matchKey = findRowKeyByLastSegmentNorm(keys, lastNorm)
  if (matchKey !== undefined) return row[matchKey]

  for (const suffix of dottedSqlFieldSuffixes(columnKey)) {
    matchKey = findRowKeyByLastSegmentNorm(keys, suffix)
    if (matchKey !== undefined) return row[matchKey]
  }

  for (const idNorm of sqlIdentifiersMatchingRowKeys(columnKey, keys)) {
    matchKey = findRowKeyByLastSegmentNorm(keys, idNorm)
    if (matchKey !== undefined) return row[matchKey]
  }

  return undefined
}

/**
 * Devuelve la clave real en `row` que debe usarse como `dataKey` en Recharts
 * cuando chartConfig usa "tabla.campo" pero la API devuelve alias (p. ej. biomasa_aerea).
 */
export function resolveRowKeyForDataKey(
  row: Record<string, unknown>,
  columnKey: string
): string {
  if (row == null || typeof row !== 'object') return columnKey

  if (Object.prototype.hasOwnProperty.call(row, columnKey)) return columnKey

  const keys = Object.keys(row)
  const colNorm = normalizeColumnKey(columnKey)

  for (const k of keys) {
    if (normalizeColumnKey(k) === colNorm) return k
  }

  const last =
    columnKey.includes('.') ? columnKey.split('.').pop()!.trim() : columnKey
  const lastNorm = normalizeColumnKey(last)

  const byLast = findRowKeyByLastSegmentNorm(keys, lastNorm)
  if (byLast !== undefined) return byLast

  for (const suffix of dottedSqlFieldSuffixes(columnKey)) {
    const k = findRowKeyByLastSegmentNorm(keys, suffix)
    if (k !== undefined) return k
  }

  for (const idNorm of sqlIdentifiersMatchingRowKeys(columnKey, keys)) {
    const k = findRowKeyByLastSegmentNorm(keys, idNorm)
    if (k !== undefined) return k
  }

  return columnKey
}
